import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, type SessionResponse } from "@numbet/protocol";
import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from "react";
import { ApiError, createRoom, joinRoom, roomInfo } from "../api.ts";
import { AvatarPicker, NameField, RoomStats, SeatPreview } from "../components/setup.tsx";
import { Footer, Page, SiteHeader } from "../components/ui.tsx";
import { errorText, t } from "../i18n.ts";
import { linkHandler, navigate } from "../router.ts";
import { loadAvatar, loadName, saveAvatar, saveName, saveSession } from "../session.ts";

function remember(session: SessionResponse, name: string, avatar: string): void {
  saveName(name.trim());
  saveAvatar(avatar);
  saveSession(session);
}

/** „Raum erstellen“ (Entwurf 4b) */
export function CreateRoom() {
  const [name, setName] = useState(loadName);
  const [avatar, setAvatar] = useState(loadAvatar);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = name.trim().length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await createRoom(name.trim(), avatar);
      remember(session, name, avatar);
      navigate(`/${session.code}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "unknown");
      setBusy(false);
    }
  };

  return (
    <>
      <SiteHeader />
      <Page>
        <div className="setup">
          <form className="stack" onSubmit={(e) => void submit(e)}>
            <a className="link small" href="/" onClick={linkHandler("/")}>
              ← {t.back}
            </a>
            <h1>{t.createRoom}</h1>
            <p className="setup-intro">{t.createIntro}</p>
            <NameField value={name} onChange={setName} autoFocus />
            <AvatarPicker value={avatar} onChange={setAvatar} name={name} />
            {error && (
              <p className="error-text" role="alert">
                ! {errorText(error)}
              </p>
            )}
            <div className="row" style={{ gap: 22 }}>
              <button className="btn btn-primary btn-large" disabled={!valid || busy}>
                {t.createRoom}
              </button>
              <a className="link" href="/beitreten" onClick={linkHandler("/beitreten")}>
                {t.insteadJoin}
              </a>
            </div>
          </form>
          <aside className="setup-aside">
            <SeatPreview name={name} avatar={avatar} host />
            <RoomStats />
          </aside>
        </div>
      </Page>
      <Footer />
    </>
  );
}

const ALLOWED = new RegExp(`^[${ROOM_CODE_ALPHABET}]$`);
type CodeStatus = "incomplete" | "checking" | "ok" | "unknown" | "running";

/** Raumcode in sechs Kästchen mit Sofortprüfung (Entwurf 4c). */
function CodeBoxes({ value, onChange, status, prefilled }: { value: string; onChange: (v: string) => void; status: CodeStatus; prefilled: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const chars = Array.from({ length: ROOM_CODE_LENGTH }, (_, i) => value[i] ?? "");

  const setAt = (i: number, char: string) => {
    const next = chars.slice();
    next[i] = char;
    onChange(next.join("").slice(0, ROOM_CODE_LENGTH));
  };

  const onInput = (i: number, raw: string) => {
    const char = raw.toUpperCase().slice(-1);
    if (char && !ALLOWED.test(char)) return;
    setAt(i, char);
    if (char && i < ROOM_CODE_LENGTH - 1) refs.current[i + 1]?.focus();
  };

  const onKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !chars[i] && i > 0) {
      setAt(i - 1, "");
      refs.current[i - 1]?.focus();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").toUpperCase().replace(new RegExp(`[^${ROOM_CODE_ALPHABET}]`, "g"), "");
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted.slice(0, ROOM_CODE_LENGTH));
    refs.current[Math.min(pasted.length, ROOM_CODE_LENGTH - 1)]?.focus();
  };

  const classes = ["code-boxes", status === "unknown" || status === "running" ? "invalid" : "", prefilled ? "prefilled" : ""].join(" ");
  return (
    <div className="field">
      <span className="field-label" id="code-label">
        {t.roomCode}
      </span>
      <div className={classes} role="group" aria-labelledby="code-label">
        {chars.map((char, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={`code-box${char ? "" : " empty"}`}
            value={char}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            aria-label={`${t.roomCode} ${i + 1}`}
            onChange={(e) => onInput(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={onPaste}
          />
        ))}
      </div>
      {status === "unknown" && <span className="error-text">! {t.codeUnknown}</span>}
      {status === "running" && <span className="error-text">! {t.gameRunning}</span>}
      {status === "ok" && prefilled && <span className="ok-text">✓ {t.codeFromLink}</span>}
    </div>
  );
}

/** „Raum beitreten“ (Entwurf 4c) – mit Code aus einem Einladungslink oder zum Eintippen. */
export function JoinRoom({ initialCode = "", fromLink = false, onJoined }: { initialCode?: string; fromLink?: boolean; onJoined?: (session: SessionResponse) => void }) {
  const [code, setCode] = useState(initialCode.toUpperCase());
  const [status, setStatus] = useState<CodeStatus>("incomplete");
  const [name, setName] = useState(loadName);
  const [avatar, setAvatar] = useState(loadAvatar);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (code.length !== ROOM_CODE_LENGTH) return setStatus("incomplete");
    let cancelled = false;
    setStatus("checking");
    roomInfo(code)
      .then((info) => !cancelled && setStatus(info.phase === "lobby" ? "ok" : "running"))
      .catch(() => !cancelled && setStatus("unknown"));
    return () => {
      cancelled = true;
    };
  }, [code]);

  const valid = status === "ok" && name.trim().length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await joinRoom(code, name.trim(), avatar);
      remember(session, name, avatar);
      if (onJoined) onJoined(session);
      else navigate(`/${session.code}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "unknown");
      setBusy(false);
    }
  };

  return (
    <>
      <SiteHeader />
      <Page>
        <div className="setup">
          <form className="stack" onSubmit={(e) => void submit(e)}>
            <a className="link small" href="/" onClick={linkHandler("/")}>
              ← {t.back}
            </a>
            <h1>{t.joinRoom}</h1>
            <p className="setup-intro">{fromLink ? t.joinIntroLink : t.joinIntro}</p>
            <CodeBoxes value={code} onChange={setCode} status={status} prefilled={fromLink} />
            <NameField value={name} onChange={setName} autoFocus={fromLink} />
            <AvatarPicker value={avatar} onChange={setAvatar} name={name} />
            {error && (
              <p className="error-text" role="alert">
                ! {errorText(error)}
              </p>
            )}
            <div className="row" style={{ gap: 22 }}>
              <button className="btn btn-cyan btn-large" disabled={!valid || busy}>
                {t.join}
              </button>
              <a className="link" href="/neu" onClick={linkHandler("/neu")}>
                {t.insteadCreate}
              </a>
            </div>
          </form>
          <aside className="setup-aside">
            <SeatPreview name={name} avatar={avatar} />
          </aside>
        </div>
      </Page>
      <Footer />
    </>
  );
}
